import '.cq{max-height:16rem;}.ssr-plugin'
import '.cr{aspect-ratio:2/1.4;}.ssr-plugin'
import '.a{display:flex;}.ssr-plugin'
import '.bb{position:relative;}.ssr-plugin'
import '.cs{max-height:27rem;}.ssr-plugin'
import '.ct{aspect-ratio:2/1;}.ssr-plugin'
import '.k{display:grid;}.ssr-plugin'
import '.a5{grid-template-columns:1fr 1fr;}.ssr-plugin'
import '.q{gap:1rem;}.ssr-plugin'
import '.cu{grid-template-columns:1.5fr 1fr;}.ssr-plugin'
import '.cv{grid-template-rows:1fr 1fr;}.ssr-plugin'
import { Style, styled } from 'inlines'
import { useTranslations } from '@i18n/useLanguage'
import Row from '@app/components/layout/Row'
import { Title } from '@app/components/core/typography/Title'
import { useState, useCallback } from 'react'
import { faImages } from '@fortawesome/pro-regular-svg-icons'
import { Button } from '@app/components/core/button/Button'
import { CloseButton } from '@app/components/core/button/CloseButton'
import { ModalWrapper } from '@app/components/blocks/modal/ModalWrapper'
import useMediaQuery from '@app/util/useMediaQuery'
import { ImagePreview } from './blocks/ImagePreview'
import { ImageDisplay } from './blocks/ImageDisplay'
import { ImageOveriew } from './blocks/ImageOverview'

export type PageMediaGalleryProps = {
  title: string
  images: string[] | { src: string }[]
  defaultImage?: string
  overviewRenderContainer?: HTMLElement
  style?: Style
}

export const PageMediaGallery = ({
  title,
  images = [],
  defaultImage,
  overviewRenderContainer,
  style,
}: PageMediaGalleryProps) => {
  const isMobile = useMediaQuery()
  const translations = useTranslations().app.mediaGallery
  const [displayedIndex, setDisplayedIndex] = useState<number>(null)
  const [overviewShown, setOverviewShown] = useState(false)
  const imagesSrc = images.map((image) =>
    typeof image === 'object' ? image.src : image,
  )

  const renderImagePreview = useCallback(
    (index: number, wrapperStyle?: Style) => {
      return (
        <ImagePreview
          key={index}
          image={imagesSrc[index]}
          index={index}
          wrapperStyle={wrapperStyle}
          imageStyle={{
            borderRadius: '1.25rem',
          }}
          onImageClick={setDisplayedIndex}
        />
      )
    },
    [imagesSrc],
  )

  const modalHeader = useCallback(
    (onClose: () => void) => (
      <Row
        style={{
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Title.H4 style={{ margin: 0 }}>{title}</Title.H4>
        <CloseButton onClick={onClose} />
      </Row>
    ),
    [title],
  )

  const renderOveriew = useCallback(() => {
    return (
      <ImageOveriew
        images={imagesSrc}
        header={modalHeader(() => setOverviewShown(false))}
        onImageClick={setDisplayedIndex}
      />
    )
  }, [imagesSrc, modalHeader])

  const goToOverview = useCallback(() => {
    setOverviewShown(true)
    setDisplayedIndex(null)
  }, [])

  const renderDisplay = useCallback(() => {
    return (
      <ImageDisplay
        displayedImage={imagesSrc[displayedIndex]}
        overviewButtonLabel={translations.allMedia}
        header={modalHeader(() => setDisplayedIndex(null))}
        canGoBack={displayedIndex !== 0}
        canGoForwad={displayedIndex !== imagesSrc.length - 1}
        goBack={() => setDisplayedIndex((prev) => prev - 1)}
        goForward={() => setDisplayedIndex((prev) => prev + 1)}
        goToOverview={imagesSrc.length > 1 ? goToOverview : undefined}
      />
    )
  }, [
    imagesSrc,
    displayedIndex,
    translations.allMedia,
    modalHeader,
    goToOverview,
  ])

  const renderModal = useCallback(() => {
    if (displayedIndex === null && !overviewShown) {
      return null
    }
    return (
      <ModalWrapper
        renderContainer={overviewRenderContainer}
        style={{
          background: 'rgba(255, 255, 255, 0.9)',
          backdropFilter: 'blur(6.5px)',
          overflowY: 'scroll',
          alignItems: displayedIndex !== null ? 'center' : 'flex-start',
        }}
      >
        {displayedIndex !== null ? renderDisplay() : renderOveriew()}
      </ModalWrapper>
    )
  }, [
    displayedIndex,
    overviewShown,
    renderDisplay,
    renderOveriew,
    overviewRenderContainer,
  ])

  // Early return after all hooks
  if (images.length === 0 && !defaultImage) {
    return null
  }

  const renderDefaultImagePreview = () => {
    return (
      <ImagePreview
        image={defaultImage}
        index={0}
        imageStyle={{
          borderRadius: '1.25rem',
        }}
        onImageClick={() => {}}
      />
    )
  }

  if (isMobile) {
    if (images.length === 0) {
      return (
        <styled.div className="cq cr a bb"
          style={{
            ...style,
          }}
        >
          {renderDefaultImagePreview()}
        </styled.div>
      )
    }

    const renderOverviewButton = images.length > 1

    return (
      <>
        {renderModal()}
        <styled.div className="cq cr a bb"
          style={{
            ...style,
          }}
        >
          {renderImagePreview(0)}
          {renderOverviewButton && (
            <Button
              style={{
                backgroundColor: 'var(--shades-white)',
                position: 'absolute',
                right: '1rem',
                bottom: '1rem',
              }}
              variant="secondary"
              size="small"
              label={translations.allMedia}
              icon={faImages}
              iconPosition="left"
              onClick={() => setOverviewShown(true)}
            />
          )}
        </styled.div>
      </>
    )
  }

  if (images.length === 0) {
    return (
      <>
        <styled.div className="cs ct"
          data-testid="page-media-gallery"
          style={{ ...style }}
        >
          {renderDefaultImagePreview()}
        </styled.div>
      </>
    )
  }

  if (images.length === 1) {
    return (
      <>
        {renderModal()}
        <styled.div className="cs ct"
          data-testid="page-media-gallery"
          style={{ ...style }}
        >
          {renderImagePreview(0)}
        </styled.div>
      </>
    )
  }

  if (images.length === 2) {
    return (
      <>
        {renderModal()}
        <styled.div className="cs ct k a5 q"
          data-testid="page-media-gallery"
          style={{
            ...style,
          }}
        >
          {renderImagePreview(0)}
          {renderImagePreview(1)}
        </styled.div>
      </>
    )
  }

  const renderOverviewButton = images.length > 1

  return (
    <>
      {renderModal()}
      <styled.div className="cs ct k cu cv q bb"
        data-testid="page-media-gallery"
        style={{
          ...style,
        }}
      >
        {renderImagePreview(0, { gridColumn: '1/2', gridRow: '1/3' })}
        {images.length > 1 &&
          renderImagePreview(1, { gridColumn: '2/3', gridRow: '1/2' })}
        {images.length > 2 &&
          renderImagePreview(2, {
            gridColumn: '2/3',
            gridRow: '2/3',
          })}
        {images
          .slice(3)
          .map((_, index) =>
            renderImagePreview(index + 3, { display: 'none' }),
          )}
        {renderOverviewButton && (
          <Button
            style={{
              backgroundColor: 'var(--shades-white)',
              position: 'absolute',
              right: '1rem',
              bottom: '1rem',
            }}
            variant="secondary"
            size="small"
            label={translations.allMedia}
            icon={faImages}
            iconPosition="left"
            onClick={() => setOverviewShown(true)}
          />
        )}
      </styled.div>
    </>
  )
}
